import React, {
	createRef,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useState
} from 'react';
import { Box, Button, Chip, MenuItem, Paper } from '@material-ui/core';
import { AttachmentType } from '../../../models';
import { Attachment } from '../../../redux';
import { Body1, AttachmentIcon, SubTitle1, VideoPlayer } from '../../atoms';

import './attachment-card.styles.scss';
import {
	ConfirmationDialog,
	ContextMenuButton,
	IConfirmationDialogProps,
	IMediaDialogProps,
	MediaDialog
} from '..';
import { getPreviewImage } from '../../../utils';
import { useTranslation } from 'react-i18next';
import { useTracker } from '../../../hooks/useTracker';

export interface IAttachmentCardProps {
	attachment: Attachment;
	isDragging?: boolean | undefined;
	isHighlighted: boolean;
	allowAuthorContentEditing?: boolean;
	onDelete: () => void;
	onEdit: (attachment: Attachment) => void;
}

export function AttachmentCard(props: IAttachmentCardProps): ReactElement {
	const {
		attachment,
		isDragging,
		allowAuthorContentEditing,
		isHighlighted,
		onDelete,
		onEdit,
	} = props;
	const tracker = useTracker()

	const { t } = useTranslation(['map', 'general']);

	const descriptionContainerRef: RefObject<HTMLDivElement> =
		createRef<HTMLDivElement>();

	const [confirmDialogProps, setConfirmDialogProps] =
		useState<IConfirmationDialogProps>({
			open: false,
			title: t('map:attachments.deleteAttachment'),
			bodyText: t('map:attachments.deleteAttachmentConfirmation'),
			onCancel: closeDialog,
			onConfirm: onConfirmDelete,
			confirmText: t('map:attachments.deleteAttachment')
		});

	const [mediaDialogProps, setMediaDialogProps] = useState<IMediaDialogProps>(
		{
			open: false,
			title: `${attachment.order}. ${attachment.title}`,
			type: attachment.type,
			source: attachment.location,
			onClose: closeMediaDialog
		}
	);

	const [descriptionState, setDescriptionState] = useState<{
		shouldClamp: boolean;
		showButton: boolean;
	}>({
		shouldClamp: true,
		showButton: true
	});

	const getUrl: () => string | undefined = useCallback<
		() => string | undefined
	>(() => {
		if (attachment.type === AttachmentType.Image)
			return getPreviewImage(attachment.location as string);
	}, [attachment.location]);

	useEffect(() => {
		if (
			descriptionContainerRef.current &&
			descriptionContainerRef.current.clientHeight < 48 &&
			descriptionState.showButton
		) {
			setDescriptionState({ ...descriptionState, showButton: false });
		}
	}, [descriptionContainerRef]);

	function closeDialog(): void {
		setConfirmDialogProps({ ...confirmDialogProps, open: false });
	}

	function closeMediaDialog(): void {
		setMediaDialogProps({ ...mediaDialogProps, open: false });
	}

	function startDelete(): void {
		setConfirmDialogProps({
			...confirmDialogProps,
			open: true
		});
	}

	function onConfirmDelete(): void {
		onDelete();
		closeDialog();
	}

	function handleAttachmentClick(): void {
		if (attachment.type === AttachmentType.Note) return;
		setMediaDialogProps({
			...mediaDialogProps,
			open: true,
			title: `${attachment.order}. ${attachment.title}`,
			source: attachment.location as string,
			type: attachment.type
		});
		tracker.elementClicked({
			module: 'attachment-side-panel',
			itemId: 'attachment-card',
			elementId: attachment.title,
			type: AttachmentType[attachment.type]
		});
	}

	return (
		<Paper
			elevation={0}
			className={`attachment-card ${isDragging ? 'dragging' : ''}${isHighlighted ? ' highlight' : ''}`}
		>
			<Box
				display="flex"
				justifyContent="space-between"
				className="attachment-card-header"
				onClick={handleAttachmentClick}
			>
				<SubTitle1>
					{attachment.order}. {attachment.title}
				</SubTitle1>
				<Box className="icon-and-context">
					<Icon />
					{(!attachment.isAuthorContent ||
						(attachment.isAuthorContent &&
							allowAuthorContentEditing)) && (
						<ContextMenuButton
							closeOnSelect
							className="attachment-context-button"
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right'
							}}
						>
							<MenuItem onClick={() => onEdit(attachment)}>
								{t('map:attachments.editAttachment')}
							</MenuItem>
							<MenuItem onClick={startDelete}>
								{t('map:attachments.deleteAttachment')}
							</MenuItem>
						</ContextMenuButton>
					)}
				</Box>
			</Box>
			<Box className="attachment-card-container">
				<Description />
				{attachment.type === AttachmentType.Video &&
					attachment.location && (
						<Box className="video-container">
							<VideoPlayer videoUrl={attachment.location} />
						</Box>
					)}
				{attachment.type === AttachmentType.Image && (
					<img src={getUrl()} alt={attachment.title} />
				)}
				{attachment.isAuthorContent && (
					<Chip label="&copy; Noordhoff" />
				)}
			</Box>
			<MediaDialog {...mediaDialogProps} />
			<ConfirmationDialog {...confirmDialogProps} />
		</Paper>
	);

	function Icon(): ReactElement {
		return (
			<Box className="icon">
				<AttachmentIcon color="#2965fb" type={attachment.type} />
			</Box>
		);
	}

	function Description(): ReactElement {
		return (
			<>
				<div
					ref={descriptionContainerRef}
					className={`description-container ${
						descriptionState.shouldClamp ? 'clamp' : ''
					}`}
				>
					<Body1>{attachment.description}</Body1>
				</div>
				{descriptionState.showButton && (
					<Button
						className="attachment-description-expand"
						variant="text"
						color="primary"
						onClick={() =>
							setDescriptionState({
								...descriptionState,
								shouldClamp: !descriptionState.shouldClamp
							})
						}
					>
						{descriptionState.shouldClamp
							? t('general:more')
							: t('general:less')}
					</Button>
				)}
			</>
		);
	}
}
